import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { resolveHomeRoute } from '@avsuite/shared';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login, isAuthenticated, isInitializing, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Already authenticated — bounce away from login.
  // Use declarative <Navigate> so the redirect happens after render, not during
  // it (calling navigate() in the render body warns about updating the router
  // while rendering Login).
  if (!isInitializing && isAuthenticated) {
    return <Navigate to={resolveHomeRoute(user?.home_route, user?.roles ?? [])} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(resolveHomeRoute(u.home_route, u.roles), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <img
              src="/brand/avsuite-wing-amber-dark.svg"
              alt="AvSuite"
              style={styles.logoAvsuite}
            />
            <span style={styles.logoDivider} aria-hidden="true" />
            <img
              src="/brand/flight-standards-logo-dark.svg"
              alt="Flight Standards"
              style={styles.logoFs}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="you@flightstandards.com.au"
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="••••••••••••"
              disabled={loading}
            />
          </div>

          {error && (
            <div style={styles.errorBox} role="alert">
              {error}
            </div>
          )}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <Link to="/forgot" style={styles.forgotLink}>Forgot password?</Link>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    background: '#1e293b',
    borderRadius: 12,
    padding: '40px 36px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: 32,
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    flexWrap: 'wrap' as const,
  },
  logoAvsuite: {
    height: 40,
    width: 'auto',
    display: 'block',
  },
  logoDivider: {
    width: 1,
    alignSelf: 'stretch',
    minHeight: 28,
    background: '#334155',
  },
  logoFs: {
    height: 46,
    width: 'auto',
    display: 'block',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#94a3b8',
  },
  input: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#f1f5f9',
    fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  errorBox: {
    padding: '10px 12px',
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 6,
    color: '#fca5a5',
    fontSize: 13,
  },
  button: {
    padding: '12px',
    background: '#3b82f6',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  forgotLink: {
    textAlign: 'center' as const,
    fontSize: 13,
    color: '#7dd3fc',
    textDecoration: 'none',
  },
} as const;
